import React, { useCallback, useEffect, useRef, useState } from "react"
import { useSelector } from "react-redux"
import { Link } from "react-router-dom"
import { selectCurrentBranch } from "../../../core/data/redux/slice/branchesSlice"
import { campusBrandName, campusBranchLabel } from "../../../utils/campusLogos"
import CommonSelect, { Option } from "../../../core/common/commonSelect"
import MultiSelectSection from "../../../core/common/multiSelect"
import Table from "../../../core/common/dataTable"
import TooltipOption from "../../../core/common/tooltipOption"
import { all_routes } from "../../router/all_routes"
import {
  SORT_OPTIONS,
  statusOptions,
} from "../../../core/common/selectoption/selectoption"
import { getAreas, AreaOption } from "../../../service/transportAreas"
import {
  Contractor,
  ContractorStatus,
  ContractorType,
  createContractor,
  getAllContractors,
  updateContractor,
} from "../../../service/contractor"
import {
  printTable,
  exportTableToPdf,
  exportTableToExcel,
} from "../../../utils/exportTable"
import { toast } from "react-toastify"

const EXPORT_HEADERS = [
  "ID",
  "Name",
  "Type",
  "CNIC",
  "Area",
  "Commission %",
  "Status",
]

const toExportRows = (rows: ContractorRow[]): string[][] =>
  rows.map(c => [
    c.identifier || c.id || "",
    c.name,
    c.type === "private" ? "Private" : "School",
    c.cnic || "",
    typeof c.area === "string" ? c.area : "",
    c.commissionPercentage != null ? String(c.commissionPercentage) : "—",
    c.status,
  ])

const formatCnic = (value: string): string => {
  const digits = value.replace(/\D/g, "").slice(0, 13)
  if (digits.length <= 5) return digits
  if (digits.length <= 12) return `${digits.slice(0, 5)}-${digits.slice(5)}`
  return `${digits.slice(0, 5)}-${digits.slice(5, 12)}-${digits.slice(12)}`
}

const CONTRACTOR_TYPE_OPTIONS: Option[] = [
  { value: "school", label: "School" },
  { value: "private", label: "Private" },
]

type ContractorRow = Contractor & {
  statusClass: string
  identifier?: string
}

const ContractorManagement: React.FC = () => {
  const routes = all_routes
  const currentBranch = useSelector(selectCurrentBranch)
  const dropdownMenuRef = useRef<HTMLDivElement | null>(null)

  const [data, setData] = useState<ContractorRow[]>([])
  const [loading, setLoading] = useState<boolean>(true)
  const [totalCount, setTotalCount] = useState(0)
  const [printLoading, setPrintLoading] = useState(false)
  const [pdfLoading, setPdfLoading] = useState(false)
  const [excelLoading, setExcelLoading] = useState(false)
  const [areaOptions, setAreaOptions] = useState<AreaOption[]>([])
  const [addSubmitting, setAddSubmitting] = useState(false)
  const [searchText, setSearchText] = useState<string>("")
  const [sortBy, setSortBy] = useState(SORT_OPTIONS[0])
  const [filters, setFilters] = useState<{ status?: Option; area?: Option }>({})
  const [appliedFilters, setAppliedFilters] = useState<{
    status?: string
    area?: string
  }>({})
  const [contractorToEdit, setContractorToEdit] =
    useState<ContractorRow | null>(null)

  const [addForm, setAddForm] = useState({
    name: "",
    areas: [] as Option[],
    cnic: "",
    type: "school" as ContractorType,
    commissionPercentage: "",
  })

  const [editForm, setEditForm] = useState({
    name: "",
    areas: [] as Option[],
    status: "active" as ContractorStatus,
    type: "school" as ContractorType,
    commissionPercentage: "",
  })

  useEffect(() => {
    getAreas().then(setAreaOptions).catch(() => {})
  }, [])

  const fetchContractors = useCallback(async () => {
    setLoading(true)
    try {
      const response = await getAllContractors({
        searchText,
        status: appliedFilters.status || "",
        area: appliedFilters.area || "",
        sortBy: sortBy.value.sortBy,
        sortOrder: sortBy.value.sortOrder,
      })

      const mapped: ContractorRow[] = response.data.map((item: any) => ({
        key: item._id,
        id: item._id,
        identifier: item.identifier || item.contractorId || item.id || item._id,
        name: item.name,
        area: Array.isArray(item.area) ? item.area.join(", ") : item.area || "",
        cnic: item.cnic || item.cnic_number,
        status: item.status as ContractorStatus,
        type: (item.type || item.contractorType || "school") as ContractorType,
        commissionPercentage: item.commissionPercentage,
        createdAt: item.createdAt,
        updatedAt: item.updatedAt,
        statusClass:
          item.status === "active" ? "badge-soft-success" : "badge-soft-danger",
      }))

      setData(mapped)
      setTotalCount(response.pagination?.total || mapped.length)
    } catch (err: any) {
      const errorMessage =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err.message ||
        "Failed to fetch contractors"
      toast.error(errorMessage)
    } finally {
      setLoading(false)
    }
  }, [
    searchText,
    appliedFilters.status,
    appliedFilters.area,
    sortBy.value.sortBy,
    sortBy.value.sortOrder,
  ])

  useEffect(() => {
    fetchContractors()
  }, [fetchContractors])

  const handleApplyFilters = () => {
    if (dropdownMenuRef.current) {
      dropdownMenuRef.current.classList.remove("show")
    }
    setAppliedFilters({
      status: filters.status?.value,
      area: filters.area?.value || "",
    })
  }

  const handleResetFilters = () => {
    setFilters({})
    setAppliedFilters({})
    if (dropdownMenuRef.current) {
      dropdownMenuRef.current.classList.remove("show")
    }
  }

  const handleSortSelect = (item: (typeof SORT_OPTIONS)[0]) => {
    setSortBy(item)
  }

  const handleAddContractor = async (e: React.FormEvent) => {
    e.preventDefault()
    if (addForm.areas.length === 0) {
      toast.error("Please select at least one area")
      return
    }
    setAddSubmitting(true)
    try {
      const areaString = addForm.areas.map(a => a.label).join(", ")
      const commissionNum =
        addForm.commissionPercentage !== "" &&
        !isNaN(Number(addForm.commissionPercentage))
          ? Math.min(100, Math.max(0, Number(addForm.commissionPercentage)))
          : undefined
      const payload = {
        name: addForm.name.trim(),
        area: areaString,
        cnic: addForm.cnic.trim(),
        status: "active" as ContractorStatus,
        type: addForm.type,
        ...(commissionNum != null && { commissionPercentage: commissionNum }),
      }

      const response = await createContractor(payload)

      if (
        response.success ||
        response._id ||
        response.id ||
        response.status === "success"
      ) {
        toast.success("Contractor added successfully!")
        setAddForm({
          name: "",
          areas: [],
          cnic: "",
          type: "school",
          commissionPercentage: "",
        })
        fetchContractors()
        const el = document.getElementById("add_contractor")
        if (el) {
          el.setAttribute("data-bs-dismiss", "modal")
          el.click()
          el.removeAttribute("data-bs-dismiss")
        }
      } else {
        const errorMessage =
          response.message || response.error || "Failed to add contractor"
        toast.error(errorMessage)
      }
    } catch (err: any) {
      const errorMessage =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err.message ||
        "Failed to add contractor"
      toast.error(errorMessage)
    } finally {
      setAddSubmitting(false)
    }
  }

  const parseAreaToOptions = (
    area: string | string[] | undefined,
  ): Option[] => {
    if (!area) return []
    if (Array.isArray(area)) {
      return area
        .map(
          label =>
            areaOptions.find(
              opt => opt.label === label || opt.value === label,
            ) || { value: label, label },
        )
        .filter(Boolean)
    }
    return area
      .split(",")
      .map(s => s.trim())
      .filter(Boolean)
      .map(
        label =>
          areaOptions.find(opt => opt.label === label) || {
            value: label,
            label,
          },
      )
  }

  const openEditModal = (contractor: ContractorRow) => {
    setContractorToEdit(contractor)
    const areasOpts = parseAreaToOptions(contractor.area as string | string[])
    const comm = contractor.commissionPercentage
    setEditForm({
      name: contractor.name,
      areas: areasOpts,
      status: contractor.status,
      type: (contractor.type || "school") as ContractorType,
      commissionPercentage: comm != null ? String(comm) : "",
    })
  }

  const handleEditContractor = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!contractorToEdit) return
    if (editForm.areas.length === 0) {
      toast.error("Please select at least one area")
      return
    }

    try {
      const areaString = editForm.areas.map(a => a.label).join(", ")
      const commissionNum =
        editForm.commissionPercentage !== "" &&
        !isNaN(Number(editForm.commissionPercentage))
          ? Math.min(100, Math.max(0, Number(editForm.commissionPercentage)))
          : undefined
      const payload = {
        name: editForm.name.trim(),
        area: areaString,
        status: editForm.status,
        type: editForm.type,
        ...(commissionNum != null && { commissionPercentage: commissionNum }),
      }

      const response = await updateContractor(contractorToEdit.id, payload)

      if (
        response.success ||
        response._id ||
        response.id ||
        response.status === "success"
      ) {
        toast.success("Contractor updated successfully!")
        setContractorToEdit(null)
        fetchContractors()
        const el = document.getElementById("edit_contractor")
        if (el) {
          el.setAttribute("data-bs-dismiss", "modal")
          el.click()
          el.removeAttribute("data-bs-dismiss")
        }
      } else {
        const errorMessage =
          response.message || response.error || "Failed to update contractor"
        toast.error(errorMessage)
      }
    } catch (err: any) {
      const errorMessage =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err.message ||
        "Failed to update contractor"
      toast.error(errorMessage)
    }
  }

  const fetchAllForExport = async (): Promise<ContractorRow[]> => {
    const total = totalCount || 500
    const response = await getAllContractors({
      searchText,
      status: appliedFilters.status || "",
      area: appliedFilters.area || "",
      sortBy: sortBy.value.sortBy,
      sortOrder: sortBy.value.sortOrder,
      limit: total,
      page: 1,
    })
    return (response.data || []).map((item: any) => ({
      key: item._id,
      id: item._id,
      identifier: item.identifier || item.contractorId || item.id || item._id,
      name: item.name,
      area: Array.isArray(item.area) ? item.area.join(", ") : item.area || "",
      cnic: item.cnic || item.cnic_number,
      status: item.status as ContractorStatus,
      type: (item.type || item.contractorType || "school") as ContractorType,
      commissionPercentage: item.commissionPercentage,
      createdAt: item.createdAt,
      updatedAt: item.updatedAt,
      statusClass:
        item.status === "active" ? "badge-soft-success" : "badge-soft-danger",
    }))
  }

  const handlePrint = async () => {
    if (!totalCount) { toast.warning("No data to print"); return }
    setPrintLoading(true)
    try {
      const all = await fetchAllForExport()
      printTable("Contractors", EXPORT_HEADERS, toExportRows(all), {
        logoUrl: currentBranch?.blackLogoUrl,
        brandName: campusBrandName(currentBranch),
        branchLabel: campusBranchLabel(currentBranch),
      })
    } catch {
      toast.error("Failed to prepare print data")
    } finally {
      setPrintLoading(false)
    }
  }

  const handleExportPdf = async () => {
    if (!totalCount) { toast.warning("No data to export"); return }
    setPdfLoading(true)
    try {
      const all = await fetchAllForExport()
      exportTableToPdf(
        "Contractors",
        EXPORT_HEADERS,
        toExportRows(all),
        "contractors.pdf",
      )
    } catch {
      toast.error("Failed to prepare PDF")
    } finally {
      setPdfLoading(false)
    }
  }

  const handleExportExcel = async () => {
    if (!totalCount) { toast.warning("No data to export"); return }
    setExcelLoading(true)
    try {
      const all = await fetchAllForExport()
      exportTableToExcel(
        all.map(c => ({
          ID: c.identifier || c.id,
          Name: c.name,
          Type: c.type === "private" ? "Private" : "School",
          CNIC: c.cnic || "",
          Area: typeof c.area === "string" ? c.area : "",
          "Commission %": c.commissionPercentage ?? "—",
          Status: c.status,
        })),
        "Contractors",
        "contractors.xlsx",
      )
    } catch {
      toast.error("Failed to prepare Excel")
    } finally {
      setExcelLoading(false)
    }
  }

  const columns = [
    {
      title: "ID",
      dataIndex: "identifier",
      render: (identifier: string, record: ContractorRow) =>
        identifier || record.id,
      sorter: (a: ContractorRow, b: ContractorRow) =>
        (a.identifier || a.id || "").localeCompare(b.identifier || b.id || ""),
    },
    {
      title: "Name",
      dataIndex: "name",
      sorter: (a: ContractorRow, b: ContractorRow) =>
        a.name.localeCompare(b.name),
    },
    {
      title: "Area",
      dataIndex: "area",
      width: 240,
      render: (area: string) => {
        const labels =
          typeof area === "string"
            ? area.split(",").map(s => s.trim()).filter(Boolean)
            : []
        if (labels.length === 0) return "—"
        return (
          <div className="d-flex flex-wrap gap-1">
            {labels.map((label, i) => (
              <span key={i} className="route-tag">{label}</span>
            ))}
          </div>
        )
      },
      sorter: (a: ContractorRow, b: ContractorRow) =>
        (a.area || "").localeCompare(b.area || ""),
    },
    {
      title: "Type",
      dataIndex: "type",
      render: (type: ContractorType) =>
        type === "private" ? "Private" : "School",
      sorter: (a: ContractorRow, b: ContractorRow) =>
        (a.type || "school").localeCompare(b.type || "school"),
    },
    {
      title: "CNIC",
      dataIndex: "cnic",
      render: (cnic: string) => {
        if (!cnic) return "—"
        const digits = cnic.replace(/\D/g, "")
        if (digits.length !== 13) return cnic
        return `${digits.slice(0, 5)}-${digits.slice(5, 12)}-${digits.slice(12)}`
      },
      sorter: (a: ContractorRow, b: ContractorRow) =>
        (a.cnic || "").localeCompare(b.cnic || ""),
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (status: ContractorStatus) => (
        <span
          className={`badge ${
            status === "active" ? "badge-soft-success" : "badge-soft-danger"
          } d-inline-flex align-items-center`}
        >
          <i className="ti ti-circle-filled fs-5 me-1"></i>
          {status === "active" ? "Active" : "Inactive"}
        </span>
      ),
      sorter: (a: ContractorRow, b: ContractorRow) =>
        a.status.localeCompare(b.status),
    },
    {
      title: "Action",
      dataIndex: "action",
      render: (_: any, record: ContractorRow) => (
        <div className="d-flex align-items-center">
          <div className="dropdown">
            <Link
              to="#"
              className="btn btn-white btn-icon btn-sm d-flex align-items-center justify-content-center rounded-circle p-0"
              data-bs-toggle="dropdown"
              aria-expanded="false"
            >
              <i className="ti ti-dots-vertical fs-14" />
            </Link>
            <ul className="dropdown-menu dropdown-menu-right p-3">
              <li>
                <Link
                  className="dropdown-item rounded-1"
                  to="#"
                  data-bs-toggle="modal"
                  data-bs-target="#edit_contractor"
                  onClick={() => openEditModal(record)}
                >
                  <i className="ti ti-edit-circle me-2" />
                  Edit
                </Link>
              </li>
              {/* No delete option by business rule (soft-controlled via status) */}
            </ul>
          </div>
        </div>
      ),
    },
  ]

  return (
    <>
      <div className="page-wrapper">
        <div className="content">
          <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
            <div className="my-auto mb-2">
              <h3 className="page-title mb-1">Contractors</h3>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={routes.adminDashboard}>Dashboard</Link>
                  </li>
                  <li className="breadcrumb-item">
                    <Link to="#">Management</Link>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    Contractors
                  </li>
                </ol>
              </nav>
            </div>
            <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
              <TooltipOption
                onRefresh={fetchContractors}
                onPrint={handlePrint}
                onExportPdf={handleExportPdf}
                onExportExcel={handleExportExcel}
                printLoading={printLoading}
                exportPdfLoading={pdfLoading}
                exportExcelLoading={excelLoading}
              />
              <div className="mb-2">
                <Link
                  to="#"
                  className="btn btn-primary"
                  data-bs-toggle="modal"
                  data-bs-target="#add_contractor"
                >
                  <i className="ti ti-square-rounded-plus me-2" />
                  Add Contractor
                </Link>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-header d-flex align-items-center justify-content-between flex-wrap pb-0">
              <h4 className="mb-3">Contractors List</h4>
              <div className="d-flex align-items-center flex-wrap">
                <div className="dropdown mb-3 me-2">
                  <Link
                    to="#"
                    className="btn btn-outline-light bg-white dropdown-toggle"
                    data-bs-toggle="dropdown"
                    data-bs-auto-close="outside"
                  >
                    <i className="ti ti-filter me-2" /> Filter
                  </Link>
                  <div
                    className="dropdown-menu drop-width"
                    ref={dropdownMenuRef}
                  >
                    <form>
                      <div className="d-flex align-items-center border-bottom p-3">
                        <h4>Filter</h4>
                      </div>
                      <div className="p-3 border-bottom">
                        <div className="mb-3">
                          <label className="form-label">Status</label>
                          <CommonSelect
                            className="select"
                            options={statusOptions}
                            value={filters.status}
                            onChange={val =>
                              setFilters(prev => ({
                                ...prev,
                                status: val as Option,
                              }))
                            }
                          />
                        </div>
                        <div className="mb-3">
                          <label className="form-label">Area</label>
                          <CommonSelect
                            className="select"
                            options={areaOptions}
                            value={filters.area}
                            onChange={val =>
                              setFilters(prev => ({
                                ...prev,
                                area: val as Option,
                              }))
                            }
                            placeholder="All areas"
                          />
                        </div>
                      </div>
                      <div className="p-3 d-flex align-items-center justify-content-end">
                        <div
                          className="btn btn-light me-3"
                          onClick={handleResetFilters}
                        >
                          Reset
                        </div>
                        <div
                          className="btn btn-primary"
                          onClick={handleApplyFilters}
                        >
                          Apply
                        </div>
                      </div>
                    </form>
                  </div>
                </div>

                <div className="dropdown mb-3">
                  <Link
                    to="#"
                    className="btn btn-outline-light bg-white dropdown-toggle"
                    data-bs-toggle="dropdown"
                  >
                    <i className="ti ti-sort-ascending-2 me-2" />
                    Sort by {sortBy.label}
                  </Link>
                  <ul className="dropdown-menu p-3">
                    {SORT_OPTIONS.map((item, index) => (
                      <li key={index}>
                        <div
                          className={`dropdown-item rounded-1 ${
                            item.label === sortBy.label ? "active" : ""
                          }`}
                          style={{ cursor: "pointer" }}
                          onClick={() => handleSortSelect(item)}
                        >
                          {item.label}
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>

            <div className="card-body p-0 py-3">
              <Table
                setSearchText={setSearchText}
                dataSource={data}
                columns={columns}
                loading={loading}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Add Contractor Modal - Add Book style theme */}
      <div
        className="modal fade"
        id="add_contractor"
        tabIndex={-1}
        aria-hidden="true"
      >
        <div
          className="modal-dialog modal-dialog-centered"
          style={{ maxWidth: 560 }}
          role="document"
        >
          <div className="modal-content rounded-3 border-0 shadow-sm">
            <div className="modal-header border-0 pb-0 pt-4 px-4">
              <h5 className="modal-title fw-semibold text-dark">
                Add Contractor
              </h5>
              <button
                type="button"
                className="btn-close bg-light rounded-circle p-2 opacity-75"
                data-bs-dismiss="modal"
                aria-label="Close"
              />
            </div>
            <form onSubmit={handleAddContractor}>
              <div className="modal-body px-4 pt-3 pb-4">
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label text-dark fw-medium mb-1">
                      Name <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control rounded-2 border border-secondary border-opacity-25"
                      placeholder="Enter contractor name"
                      value={addForm.name}
                      onChange={e =>
                        setAddForm(prev => ({
                          ...prev,
                          name: e.target.value,
                        }))
                      }
                      required
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label text-dark fw-medium mb-1">
                      CNIC <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      value={addForm.cnic}
                      onChange={e =>
                        setAddForm(prev => ({
                          ...prev,
                          cnic: formatCnic(e.target.value),
                        }))
                      }
                      className={`form-control rounded-2 border border-secondary border-opacity-25${
                        addForm.cnic && !/^\d{5}-\d{7}-\d$/.test(addForm.cnic)
                          ? " is-invalid"
                          : ""
                      }`}
                      placeholder="12345-1234567-1"
                      required
                    />
                    {addForm.cnic &&
                      addForm.cnic.replace(/[_\s]/g, "").length > 0 &&
                      !/^\d{5}-\d{7}-\d$/.test(addForm.cnic) && (
                        <div className="invalid-feedback">
                          Enter a valid CNIC in format: 12345-1234567-1
                        </div>
                      )}
                  </div>
                  <div className={addForm.type !== "private" ? "col-md-6" : "col-12"}>
                    <label className="form-label text-dark fw-medium mb-1">
                      Contractor Type
                    </label>
                    <CommonSelect
                      className="select"
                      options={CONTRACTOR_TYPE_OPTIONS}
                      value={
                        CONTRACTOR_TYPE_OPTIONS.find(
                          o => o.value === addForm.type,
                        ) || null
                      }
                      onChange={opt =>
                        setAddForm(prev => ({
                          ...prev,
                          type: (opt?.value as ContractorType) || "school",
                        }))
                      }
                      placeholder="Select type"
                    />
                  </div>
                  {addForm.type !== "private" && (
                    <div className="col-md-6">
                      <label className="form-label text-dark fw-medium mb-1">
                        Commission % (School share)
                      </label>
                      <input
                        type="number"
                        className="form-control rounded-2 border border-secondary border-opacity-25"
                        min={0}
                        max={100}
                        step={0.01}
                        placeholder="0–100"
                        value={addForm.commissionPercentage}
                        onChange={e => {
                          const raw = e.target.value
                          const num = parseFloat(raw)
                          setAddForm(prev => ({
                            ...prev,
                            commissionPercentage:
                              raw === "" ? "" : !isNaN(num) && num > 100 ? "100" : raw,
                          }))
                        }}
                      />
                    </div>
                  )}
                  <div className="col-12">
                    <label className="form-label text-dark fw-medium mb-1">
                      Area <span className="text-danger">*</span>
                    </label>
                    <div className="contractor-multiselect-wrapper rounded-2 border border-secondary border-opacity-25">
                      <MultiSelectSection
                        options={areaOptions}
                        value={addForm.areas}
                        onChange={selected =>
                          setAddForm(prev => ({
                            ...prev,
                            areas: selected,
                          }))
                        }
                        placeholder="Select areas (Karachi)"
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer border-0 pt-0 pb-4 px-4 d-flex justify-content-end gap-2">
                <button
                  type="button"
                  className="btn btn-light rounded-2 border border-secondary border-opacity-25 text-dark px-3"
                  data-bs-dismiss="modal"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary rounded-2 px-4"
                  disabled={addSubmitting}
                >
                  {addSubmitting ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                      Adding...
                    </>
                  ) : (
                    "Add Contractor"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Edit Contractor Modal - Add Book style theme */}
      <div
        className="modal fade"
        id="edit_contractor"
        tabIndex={-1}
        aria-hidden="true"
      >
        <div
          className="modal-dialog modal-dialog-centered"
          style={{ maxWidth: 560 }}
          role="document"
        >
          <div className="modal-content rounded-3 border-0 shadow-sm">
            <div className="modal-header border-0 pb-0 pt-4 px-4">
              <h5 className="modal-title fw-semibold text-dark">
                Edit Contractor
              </h5>
              <button
                type="button"
                className="btn-close bg-light rounded-circle p-2 opacity-75"
                data-bs-dismiss="modal"
                aria-label="Close"
              />
            </div>
            <form onSubmit={handleEditContractor}>
              <div className="modal-body px-4 pt-3 pb-4">
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label text-dark fw-medium mb-1">
                      Name <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control rounded-2 border border-secondary border-opacity-25"
                      placeholder="Enter contractor name"
                      value={editForm.name}
                      onChange={e =>
                        setEditForm(prev => ({
                          ...prev,
                          name: e.target.value,
                        }))
                      }
                      required
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label text-dark fw-medium mb-1">
                      CNIC
                    </label>
                    <input
                      type="text"
                      className="form-control rounded-2 border border-secondary border-opacity-25 bg-light"
                      value={contractorToEdit?.cnic || ""}
                      disabled
                      readOnly
                    />
                  </div>
                  <div className={editForm.type !== "private" ? "col-md-6" : "col-12"}>
                    <label className="form-label text-dark fw-medium mb-1">
                      Contractor Type
                    </label>
                    <CommonSelect
                      className="select"
                      options={CONTRACTOR_TYPE_OPTIONS}
                      value={
                        CONTRACTOR_TYPE_OPTIONS.find(
                          o => o.value === editForm.type,
                        ) || null
                      }
                      onChange={opt =>
                        setEditForm(prev => ({
                          ...prev,
                          type: (opt?.value as ContractorType) || "school",
                        }))
                      }
                      placeholder="Select type"
                    />
                  </div>
                  {editForm.type !== "private" && (
                    <div className="col-md-6">
                      <label className="form-label text-dark fw-medium mb-1">
                        Commission % (School share)
                      </label>
                      <input
                        type="number"
                        className="form-control rounded-2 border border-secondary border-opacity-25"
                        min={0}
                        max={100}
                        step={0.01}
                        placeholder="0–100"
                        value={editForm.commissionPercentage}
                        onChange={e => {
                          const raw = e.target.value
                          const num = parseFloat(raw)
                          setEditForm(prev => ({
                            ...prev,
                            commissionPercentage:
                              raw === "" ? "" : !isNaN(num) && num > 100 ? "100" : raw,
                          }))
                        }}
                      />
                    </div>
                  )}
                  <div className="col-12">
                    <label className="form-label text-dark fw-medium mb-1">
                      Area <span className="text-danger">*</span>
                    </label>
                    <div className="contractor-multiselect-wrapper rounded-2 border border-secondary border-opacity-25">
                      <MultiSelectSection
                        options={areaOptions}
                        value={editForm.areas}
                        onChange={selected =>
                          setEditForm(prev => ({
                            ...prev,
                            areas: selected,
                          }))
                        }
                        placeholder="Select areas (Karachi)"
                      />
                    </div>
                  </div>
                  <div className="col-12 pt-2">
                    <div className="d-flex align-items-center justify-content-between">
                      <div>
                        <span className="form-label text-dark fw-medium mb-0 d-block">
                          Status
                        </span>
                        <p className="text-muted small mb-0 mt-0">
                          Change the Status by toggle
                        </p>
                      </div>
                      <div className="form-check form-switch mb-0">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          role="switch"
                          id="edit_contractor_status"
                          checked={editForm.status === "active"}
                          onChange={e =>
                            setEditForm(prev => ({
                              ...prev,
                              status: e.target.checked ? "active" : "inactive",
                            }))
                          }
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer border-0 pt-0 pb-4 px-4 d-flex justify-content-end gap-2">
                <button
                  type="button"
                  className="btn btn-light rounded-2 border border-secondary border-opacity-25 text-dark px-3"
                  data-bs-dismiss="modal"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary rounded-2 px-4"
                >
                  Update
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  )
}

export default ContractorManagement
